import type { Bemanning, TalentBehovItem } from '../services/api';

/**
 * Status Helpers for Bemanning/Talent
 * 
 * Pure functions for calculating staffing status and metrics.
 * Extracted from ResourcePlanning.tsx and TalentManagerConcept.tsx.
 */

/**
 * Status for en talent (needed vs assigned/confirmed/declined)
 */
export interface TaskStatus {
  confirmed: number;  // Antall bekreftede
  waiting: number;    // Antall planlagte/ikke svart
  declined: number;   // Antall avvist
  total: number;      // Totalt antall tilordnet
  needed: number;     // Antall som trengs
}

/**
 * Aggregert status for en kategori (sum av alle talents)
 */
export interface CategoryStatus {
  totalConfirmed: number;
  totalWaiting: number;
  totalDeclined: number;
  totalNeeded: number;
}

/**
 * Beregner status for en talent (hvor mange bekreftede, ventende, avviste, etc)
 * 
 * @param task - Talent-behov
 * @param bemanning - Liste over bemanning
 * @returns Status med tellere
 */
export const getTaskStatus = (
  task: TalentBehovItem,
  bemanning: Bemanning[]
): TaskStatus => {
  const peopleForTask = bemanning.filter(b => b.talent_navn === task.talent_navn);
  const confirmed = peopleForTask.filter(p => p.status === 'bekreftet').length;
  const waiting = peopleForTask.filter(p => p.status === 'planlagt' || p.status === 'ikke_svart' || !p.status).length;
  const declined = peopleForTask.filter(p => p.status === 'avvist').length;
  const total = peopleForTask.length;
  const needed = task.antall;
  
  return { confirmed, waiting, declined, total, needed };
};

/**
 * Beregner aggregert status for en kategori (sum av alle talents)
 * 
 * @param categoryTasks - Liste over talent-behov i kategorien
 * @param bemanning - Liste over bemanning
 * @returns Aggregert status
 */
export const getCategoryStatus = (
  categoryTasks: TalentBehovItem[],
  bemanning: Bemanning[]
): CategoryStatus => {
  let totalConfirmed = 0;
  let totalWaiting = 0;
  let totalDeclined = 0;
  let totalNeeded = 0;
  
  categoryTasks.forEach(task => {
    const status = getTaskStatus(task, bemanning);
    totalConfirmed += status.confirmed;
    totalWaiting += status.waiting;
    totalDeclined += status.declined;
    totalNeeded += status.needed;
  });
  
  return { totalConfirmed, totalWaiting, totalDeclined, totalNeeded };
};

/**
 * Teller antall personer tilordnet et talent (brukes i TalentManagerConcept)
 * 
 * @param talentNavn - Navn på talent
 * @param bemanning - Liste over bemanning
 * @returns Antall personer tilordnet
 */
export const countAssignedForTalent = (
  talentNavn: string,
  bemanning: Bemanning[]
): number => {
  return bemanning.filter(b => b.talent_navn === talentNavn).length;
};

/**
 * Teller antall bekreftede personer for et talent (brukes i TalentManagerConcept)
 * 
 * @param talentNavn - Navn på talent
 * @param bemanning - Liste over bemanning
 * @returns Antall bekreftede personer
 */
export const countConfirmedForTalent = (
  talentNavn: string,
  bemanning: Bemanning[]
): number => {
  return bemanning.filter(b => b.talent_navn === talentNavn && b.status === 'bekreftet').length;
};

